import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ContactReferralsResult, type ReferralProgrammeResult } from '@whinerp/client';
import { formatMoney } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * A referral programme (G-164), from both ends: on a contact, the code they
 * hand out and who they have referred; on a lead, who referred them.
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';

export function ContactReferralsPanel({ contactId }: { contactId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const enabled = useEntitlement('F-062').enabled;
  const key = ['referrals', contactId];
  const summary = useQuery<ContactReferralsResult, ApiError>({
    queryKey: key,
    queryFn: () => api.contactReferrals({ path: { contactId } }),
    enabled,
  });
  const programme = useQuery<ReferralProgrammeResult, ApiError>({
    queryKey: ['referral-programme'],
    queryFn: () => api.referralProgramme({}),
    enabled,
  });
  const issue = useMutation({
    mutationFn: () => api.issueReferralCode({ path: { contactId } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
  });
  const pay = useMutation({
    mutationFn: (referralId: string) => api.markReferralRewardPaid({ body: { referralId } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
  });

  if (!enabled || !summary.data || !programme.data) return null;
  if (!programme.data.active && summary.data.referrals.length === 0) return null;
  const view = summary.data;
  const money = (amount: string | null, currency: string | null) =>
    amount === null ? '-' : formatMoney(amount, format, { currency: currency ?? programme.data.currency });

  return (
    <Panel data-region="referrals">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('referrals.title')}</SectionLabel>
        {view.code ? (
          <p className="text-dense text-ink">
            {t('referrals.code')} <span className="font-mono text-ink" data-referral-code>{view.code}</span>
          </p>
        ) : (
          <div>
            <Button size="small" data-referral-issue disabled={issue.isPending} onClick={() => issue.mutate()}>
              {t('referrals.issue')}
            </Button>
          </div>
        )}
        {view.referrals.length === 0 ? (
          <p className="text-meta text-ink-meta">{t('referrals.none')}</p>
        ) : (
          <ul className="flex flex-col gap-1 text-dense">
            {view.referrals.map((entry) => (
              <li key={entry.referralId} data-referral={entry.referralId} data-referral-status={entry.status}
                className="flex flex-wrap items-center gap-2">
                <span className="text-ink">{entry.name || '-'}</span>
                <span className="text-ink-meta">{t(`referrals.status.${entry.status}`)}</span>
                {entry.status === 'won' ? <span className="text-ink-600">{money(entry.wonValue, entry.wonCurrency)}</span> : null}
                {entry.rewardStatus !== 'none' ? (
                  <span className="ms-auto text-ink" data-referral-reward={entry.rewardStatus}>
                    {t(`referrals.reward.${entry.rewardStatus}`, { values: { amount: money(entry.rewardAmount, entry.rewardCurrency) } })}
                  </span>
                ) : null}
                {entry.rewardStatus === 'pending' && can('revenue.manage') ? (
                  <Button size="small" data-referral-pay={entry.referralId} onClick={() => pay.mutate(entry.referralId)}>
                    {t('referrals.markPaid')}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {pay.error ? <p className="text-dense text-danger-text">{errorText(pay.error as ApiError)}</p> : null}
      </div>
    </Panel>
  );
}

export function LeadReferralPanel({ leadId }: { leadId: string }): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const enabled = useEntitlement('F-062').enabled;
  const [code, setCode] = useState('');
  const record = useMutation({ mutationFn: () => api.recordReferral({ path: { leadId }, body: { code } }) });
  if (!enabled) return null;
  return (
    <Panel data-region="lead-referral">
      <form className="flex flex-wrap items-end gap-2" data-lead-referral
        onSubmit={(event) => {
          event.preventDefault();
          record.mutate();
        }}>
        <label className="flex flex-col gap-1 text-meta text-ink-meta">
          {t('referrals.referredBy')}
          <input className={`${INPUT} w-32 uppercase`} required data-lead-referral-code value={code} onChange={(event) => setCode(event.target.value)} />
        </label>
        <Button size="small" type="submit" data-lead-referral-save disabled={record.isPending}>{t('referrals.link')}</Button>
        {record.data ? (
          <span className="text-dense text-ink" data-lead-referrer>{t('referrals.linked', { values: { name: record.data.referrerName } })}</span>
        ) : null}
        {record.error ? <p className="w-full text-dense text-danger-text" data-lead-referral-error>{errorText(record.error as ApiError)}</p> : null}
      </form>
    </Panel>
  );
}

export function ReferralProgrammePanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const enabled = useEntitlement('F-062').enabled && can('campaign.send');
  const programme = useQuery<ReferralProgrammeResult, ApiError>({
    queryKey: ['referral-programme'],
    queryFn: () => api.referralProgramme({}),
    enabled,
  });
  const [draft, setDraft] = useState<ReferralProgrammeResult | null>(null);
  const save = useMutation({
    mutationFn: (body: ReferralProgrammeResult) => api.saveReferralProgramme({ body }),
    onSuccess: (result) => {
      queryClient.setQueryData(['referral-programme'], result);
      setDraft(null);
    },
  });
  if (!enabled || !programme.data) return null;
  const value = draft ?? programme.data;
  return (
    <Panel data-region="referral-programme">
      <form className="flex flex-wrap items-end gap-3" data-referral-programme
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate(value);
        }}>
        <SectionLabel>{t('referrals.programme')}</SectionLabel>
        <label className="flex items-center gap-2 text-dense text-ink">
          <input type="checkbox" data-programme-active checked={value.active} onChange={(event) => setDraft({ ...value, active: event.target.checked })} />
          {t('referrals.active')}
        </label>
        <select className={INPUT} data-programme-kind value={value.rewardKind} aria-label={t('referrals.rewardKind')}
          onChange={(event) => setDraft({ ...value, rewardKind: event.target.value as 'fixed' | 'percent' })}>
          <option value="fixed">{t('referrals.kind.fixed')}</option>
          <option value="percent">{t('referrals.kind.percent')}</option>
        </select>
        <input className={`${INPUT} w-28`} inputMode="decimal" data-programme-amount value={value.rewardAmount} aria-label={t('referrals.amount')}
          onChange={(event) => setDraft({ ...value, rewardAmount: event.target.value })} />
        {value.rewardKind === 'fixed' ? (
          <input className={`${INPUT} w-20`} maxLength={3} data-programme-currency value={value.currency} aria-label={t('referrals.currency')}
            onChange={(event) => setDraft({ ...value, currency: event.target.value.toUpperCase() })} />
        ) : null}
        <Button size="small" type="submit" data-programme-save disabled={save.isPending || draft === null}>{t('referrals.save')}</Button>
        {save.error ? <p className="w-full text-dense text-danger-text">{errorText(save.error as ApiError)}</p> : null}
      </form>
    </Panel>
  );
}
